import axios, { AxiosResponse } from "axios"
import { init } from "@/core/init"
import { reportAddToCart, nostojs } from "./api"
import { shopifyAddMultipleToCart, shopifyAddToCart, shopifyAddToCartWithDiscount } from "./cart/variant"
import addProductToCart from "./cart/product"
import multicurrency from "./currency"
import skuSelection from "./sku-selection"
import { updateCartTagging } from "./tagging"
import { fetchPresentmentPrice, setPresentmentPrices, setPresentmentPricesUrl } from "./presentmentPrice"
import migrateToShopifyMarket from "./markets"
import bundleDiscount from "./cart/actions/bundled/bundled-discount"
import { ProductIdentifier, Cart, CartDetail, ProductAddToCart, CartProduct } from "./types"
import { checkTokenToCustomer, sendCartToken } from "./script-tag"
import { SlotReference } from "@/utils/resolveSlotId"
import { RecommendationRequestFlags } from "@nosto/client-lib"

export async function addSkuToCart(identifier: ProductIdentifier, slotIdOrElement?: SlotReference, quantity = 1) {
  await shopifyAddToCart(identifier.skuId, quantity)
  if (slotIdOrElement) {
    await reportAddToCart(identifier, slotIdOrElement)
  }
}

/**
 * Add multiple products to the cart
 * @param {{productId: String, skuId: String, quantity: integer}[]} products
 * @param {Node} element containing slot id
 */
export async function addMultipleProductsToCart(products: ProductAddToCart[] = [], element?: SlotReference) {
  await shopifyAddMultipleToCart(products)
  if (element) {
    await Promise.all(products.map(product => reportAddToCart(product, element)))
  }
}

let cartPromise: Promise<AxiosResponse<Cart>> | undefined = undefined

async function handleReloadCart() {
  if (!cartPromise) {
    cartPromise = axios.get<Cart>("/cart.js")
    setTimeout(() => (cartPromise = undefined), 1000)
  }
  return toNostoCart((await cartPromise).data)
}

function toNostoCart(cart: Cart): Cart {
  if (cart.item_count === 0) {
    console.info("No items found in the cart")
    return { items: [] }
  } else {
    return {
      currency: cart.currency,
      total_discount: cart.total_discount,
      items: cart.items.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
        title: item.title,
        // The line item price in the cart is in cents for some reason.
        price: (+item.price / 100).toFixed(2)
      }))
    }
  }
}

export async function reloadCart(elements: string[], flags: RecommendationRequestFlags = { reloadCart: true }) {
  const nostoCartData = await handleReloadCart()
  updateCartTagging(nostoCartData)
  if (!nostoCartData.items.length) {
    // only trigger ev1, if there are items in the cart
    return
  }
  nostojs(async api => {
    const request = api.createRecommendationRequest({ includeTagging: true })
    if (elements) {
      request.setElements(elements)
    }
    await request.load({ skipPageViews: true, ...flags })
  })
}

export async function reloadCartWithFlags(flags: RecommendationRequestFlags, elements: string[]) {
  return reloadCart(elements, flags)
}

function toProductIdentifier({ productId, variantId }: CartProduct) {
  return { productId, skuId: variantId }
}

/**
 * Calculate discounts on cart items and then invokes add.js shopify API to update the cart information
 * @param { CartDetail } cartDetail
 * @param { Node } element
 */
async function addBundleToCartWithDiscount(cartDetail: CartDetail, element: SlotReference) {
  const payload = bundleDiscount(cartDetail)
  await shopifyAddToCartWithDiscount(payload)
  return await Promise.all(cartDetail.items.map(toProductIdentifier).map(item => reportAddToCart(item, element)))
}

export default function initCartScript() {
  multicurrency()
  checkTokenToCustomer()
  if (typeof window.Nosto === "undefined") {
    window.Nosto = {}
  }
  Object.assign(window.Nosto, {
    addProductToCart,
    addSkuToCart,
    addMultipleProductsToCart,
    reloadCart,
    reloadCartWithFlags,
    sendCartToken,
    setPresentmentPrices,
    setPresentmentPricesUrl,
    fetchPresentmentPrice,
    migrateToShopifyMarket,
    addBundleToCartWithDiscount,
    shopifyScript: true
  })

  skuSelection(window)

  void init(window)
}
